import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../hooks/useTenant";
import { useSelector } from "react-redux";
import { selectSettings } from "../../store/slices/settingsSlice";
import { useTheme } from "../../context/ThemeContext";
import { getCardBackground } from "../../utils/themeUtils";
import { performLogout } from "../../utils/logout";
import { captureError } from "../../utils/sentry";
import { safeGetItem } from "@mediatechgroup/app-version";

interface NavbarProps {
  transparent?: boolean;
  linkColor?: string;
  hoverColor?: string;
}

const Navbar: React.FC<NavbarProps> = ({
  transparent = false,
  linkColor,
  hoverColor,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  useTenant();
  safeGetItem("settings");
  const settings: any = useSelector(selectSettings);
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract tenant ID dynamically from current URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  const handleLogout = async () => {
    try {
      // Use centralized logout utility - clears all user data, preserves tenant info
      const tenantSlug = await performLogout();
      // Redirect to login
      navigate(`/${tenantSlug || tenantId}/login`);
    } catch (error) {
      captureError(error, { component: "Navbar", operation: "signOut" });
    }
  };

  return (
    <nav className="w-full z-30 bg-gray-900">
      <div className="container mx-auto px-6 py-3 flex justify-between items-center">
        <Link to={`/${tenantId}`} className="flex items-center">
          {/* Fixed-height slot reserves layout space so swapping the logo
              image in after async settings load doesn't produce a ~52px
              CLS shift on the topmost element of the page. */}
          <div className="h-20 flex items-center">
            {settings?.logo_url ? (
              <img
                src={settings.logo_url}
                alt={`${settings?.business_name || "Club"} logo`}
                className="h-20 w-auto rounded-md object-contain"
                width={200}
                height={80}
              />
            ) : (
              <span
                className="text-xl font-bold"
                style={{ color: "#ffffff" }}
              >
                {settings?.business_name || "Club"}
              </span>
            )}
          </div>
        </Link>

        <div className="hidden md:flex items-center space-x-8">
          <Link to={`/${tenantId}`} className={`text-white`}>
            Home
          </Link>
          <Link to={`/${tenantId}/programs`} className={`text-white`}>
            Programs
          </Link>
          <Link to={`/${tenantId}/camps`} className={`text-white`}>
            Camps
          </Link>
          <Link to={`/${tenantId}/about`} className={`text-white`}>
            About
          </Link>
          {settings?.blog_enabled && (
            <Link to={`/${tenantId}/posts`} className={`text-white`}>
              Blog
            </Link>
          )}
          <Link to={`/${tenantId}/contact`} className={`text-white`}>
            Contact
          </Link>

          {user ? (
            <div className="relative group inline-block">
              <div className="flex items-center space-x-1 focus:outline-none cursor-pointer">
                <span
                  className={`${
                    linkColor ?? (transparent ? "text-white" : "text-gray-800")
                  } ${hoverColor ?? "hover:text-indigo-600"}`}
                >
                  My Account
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <div className={`absolute right-0 mt-2 w-48 ${getCardBackground(isDarkMode)} rounded-md shadow-lg py-1 z-50 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200`}>
                <Link
                  to={`/${tenantId}/dashboard`}
                  className="block px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    color: isDarkMode ? '#4b5563' : '#6b7280',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = isDarkMode ? '#4b5563' : '#6b7280';
                  }}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/${tenantId}/member/manage-profile`}
                  className="block px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    color: isDarkMode ? '#4b5563' : '#6b7280',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = isDarkMode ? '#4b5563' : '#6b7280';
                  }}
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    backgroundColor: isDarkMode ? '#4b5563' : '#6b7280',
                    color: "#ffffff",
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <Link
                to={`/${tenantId}/login`}
                className="px-4 py-2 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: "#ffffff",
                  color: isDarkMode ? '#4b5563' : '#6b7280',
                }}
              >
                Sign In
              </Link>
              <Link
                to={`/${tenantId}/register`}
                className="px-4 py-2 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: isDarkMode ? '#4b5563' : '#6b7280',
                  color: "#ffffff",
                }}
              >
                Register
              </Link>
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden flex items-center">
          <button
            className="outline-none mobile-menu-button p-2 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors duration-200 border border-white border-opacity-30"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg
              className={`w-6 h-6 transition-transform duration-200 text-white ${
                isMobileMenuOpen ? "rotate-90" : ""
              }`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`mobile-menu md:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? "block" : "hidden"
        }`}
      >
        <ul className="px-4 py-2 shadow-lg bg-gray-900">
          <li>
            <Link
              to={`/${tenantId}`}
              className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/programs`}
              className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Programs
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/camps`}
              className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Camps
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/about`}
              className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
          </li>
          {settings?.blog_enabled && (
            <li>
              <Link
                to={`/${tenantId}/posts`}
                className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
            </li>
          )}
          <li>
            <Link
              to={`/${tenantId}/contact`}
              className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </li>
          {user ? (
            <>
              <li>
                <Link
                  to={`/${tenantId}/dashboard`}
                  className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to={`/${tenantId}/member/manage-profile`}
                  className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                >
                  Sign Out
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link
                  to={`/${tenantId}/login`}
                  className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              </li>
              <li>
                <Link
                  to={`/${tenantId}/register`}
                  className="block px-4 py-3 text-white hover:bg-black hover:bg-opacity-20 rounded-md transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
